import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../AuthProvider";
import { ScreenContext } from "../../ScreenProvider";
import Parse from "parse";

const OperationalStatus = () => {
    const { esp } = useContext(AuthContext);
    const { isMobile } = useContext(ScreenContext);
    const [status, setStatus] = useState({
        POWER1: null,
        POWER2: null,
        heatingGoalTemp: null,
    });
    const [loading, setLoading] = useState(true);
    const [showHeatingTooltip, setShowHeatingTooltip] = useState(false);
    const [showHotWaterTooltip, setShowHotWaterTooltip] = useState(false);
    const heatingRef = useRef(null);
    const hotWaterRef = useRef(null);

    useEffect(() => {
        if (!esp?.id) {
            setLoading(false);
            return;
        }

        const fetchStatus = async () => {
            try {
                const result = await Parse.Cloud.run("getOperationalStatus", { espId: esp.id });
                if (result) {
                    setStatus(result);
                }
            } catch (error) {
                console.error("Fel vid hämtning av driftstatus:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();

        // Uppdatera status var 30:e sekund
        const interval = setInterval(fetchStatus, 30000);

        return () => clearInterval(interval);
    }, [esp?.id]);

    // Stäng tooltips när man klickar utanför
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile) {
                if (heatingRef.current && !heatingRef.current.contains(event.target)) {
                    setShowHeatingTooltip(false);
                }
                if (hotWaterRef.current && !hotWaterRef.current.contains(event.target)) {
                    setShowHotWaterTooltip(false);
                }
            }
        };

        if (isMobile) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isMobile]);

    if (loading || !esp) {
        return null;
    }

    // Bygg textsträngen för värmning med måltemp
    const getHeatingText = () => {
        if (status.POWER1 === null) return null;
        const heatingStatus = status.POWER1 ? 'Värmning pågår' : 'Värmning av';
        if (status.heatingGoalTemp !== null) {
            return `${heatingStatus}, måltemp: ${status.heatingGoalTemp.toFixed(1)}°C`;
        }
        return heatingStatus;
    };

    const getHotWaterText = () => {
        if (status.POWER2 === null) return null;
        return status.POWER2 ? 'Varmvattenvärmning pågår' : 'Varmvattenvärmning av';
    };

    const heatingText = getHeatingText();
    const hotWaterText = getHotWaterText();

    return (
        <div className="operational-status" style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: isMobile ? '6px' : '12px',
            padding: isMobile ? '4px 0' : '0.6em 1.2em',
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0',
            fontSize: isMobile ? '8px' : '1em',
            height: isMobile ? '40px' : 'auto',
            lineHeight: isMobile ? 'normal' : '1.5',
            width: isMobile ? 'auto' : 'auto',
            minWidth: isMobile ? 'fit-content' : 'auto',
            margin: isMobile ? '0' : '0',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            fontWeight: isMobile ? 'normal' : '500',
            flexShrink: 0,
            zIndex: isMobile ? 10 : 'auto',
            position: isMobile ? 'relative' : 'static',
            overflow: 'visible',
        }}>
            {heatingText && (
                <div 
                    ref={heatingRef}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: isMobile ? '4px' : '6px', 
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        cursor: isMobile ? 'pointer' : 'default',
                        minWidth: isMobile ? '24px' : 'auto',
                        minHeight: isMobile ? '24px' : 'auto',
                    }}
                    onClick={() => isMobile && setShowHeatingTooltip(!showHeatingTooltip)}
                >
                    <i className={`bi ${status.POWER1 ? 'bi-fire' : 'bi-fire'}`} 
                       style={{ 
                           color: status.POWER1 ? '#FFB405' : '#666666', 
                           fontSize: isMobile ? '20px' : '1em',
                           display: 'block',
                           lineHeight: '1',
                       }} />
                    {!isMobile && <span>{heatingText}</span>}
                    {isMobile && showHeatingTooltip && (
                        <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            right: '0',
                            marginBottom: '8px',
                            padding: '8px 12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            borderRadius: '6px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            maxWidth: 'calc(100vw - 20px)',
                        }}>
                            {heatingText}
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: '12px',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '6px solid rgba(0, 0, 0, 0.85)',
                            }} />
                        </div>
                    )}
                </div>
            )}
            {hotWaterText && (
                <div 
                    ref={hotWaterRef}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: isMobile ? '4px' : '6px', 
                        whiteSpace: 'nowrap',
                        paddingLeft: heatingText ? (isMobile ? '8px' : '12px') : '0',
                        position: 'relative',
                        cursor: isMobile ? 'pointer' : 'default',
                        minWidth: isMobile ? '24px' : 'auto',
                        minHeight: isMobile ? '24px' : 'auto',
                    }}
                    onClick={() => isMobile && setShowHotWaterTooltip(!showHotWaterTooltip)}
                >
                    <i className={`bi ${status.POWER2 ? 'bi-droplet-fill' : 'bi-droplet'}`} 
                       style={{ 
                           color: status.POWER2 ? '#FF8C00' : '#666666', 
                           fontSize: isMobile ? '20px' : '1em',
                           display: 'block',
                           lineHeight: '1',
                       }} />
                    {!isMobile && <span>{hotWaterText}</span>}
                    {isMobile && showHotWaterTooltip && (
                        <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            right: '0',
                            marginBottom: '8px',
                            padding: '8px 12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            borderRadius: '6px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            pointerEvents: 'none',
                            maxWidth: 'calc(100vw - 20px)',
                        }}>
                            {hotWaterText}
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: '12px',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '6px solid rgba(0, 0, 0, 0.85)',
                            }} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OperationalStatus;
